import { AnyElement, ReadInBrowserEnum, clamp } from "@zyzgroup/core-common";
import {
  EMPTY,
  Observable,
  combineLatest,
  firstValueFrom,
  from,
  fromEventPattern,
  iif,
  merge,
  of,
  race
} from "rxjs";
import {
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  skipWhile,
  startWith,
  switchAll,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
  windowToggle
} from "rxjs/operators";
import Hammer from "hammerjs";
import {
  useEvent$,
  HTMLEventObservable,
  stopPropagation,
  preventDefault
} from "./useEventListener";
import { atomWriteAttribute } from "./useAttribute";
import { useSelectFile } from "./useElement";
import { atomReadStyle, atomReadStyles, readStyleFromStyles } from "./useStyle";

declare const document: Document & {
  attachEvent: any;
  createEventObject: any;
  onmousewheel: any;
};

export function isSupportTouch(): boolean {
  return (
    "ontouchstart" in window ||
    "ontouchend" in document ||
    navigator.maxTouchPoints > 0
  );
}

export function isSupportPointer(): boolean {
  // return !!(window.PointerEvent || (window as any).MSPointerEvent); // IE10 is prefixed
  return !!window.PointerEvent;
}

/*
 * Detects whether the event "eventName" is supported on a tag with name
 * "nodeName".  Based on code from
 * http://perfectionkills.com/detecting-event-support-without-browser-sniffing/
 */
export function isSupportEvent(nodeName: any, eventName: string): boolean {
  let el = document.createElement(nodeName);
  eventName = "on" + eventName;
  let isSupported = eventName in el;
  if (!isSupported) {
    el.setAttribute(eventName, "return;");
    isSupported = typeof el[eventName] == "function";
  }
  el = null;
  return isSupported;
}

export type MousePoint = {
  x: number;
  y: number;
  isDown: boolean; // 是否已按下
};

export type MousePointGroup = {
  start: MousePoint;
  current: MousePoint;
};

export function createDefaultMousePoint() {
  return {
    x: 0,
    y: 0,
    isDown: false
  };
}

export function useMousePoint() {
  const startMousePoint: MousePoint = createDefaultMousePoint();
  const currentMousePoint: MousePoint = createDefaultMousePoint();

  return {
    start: startMousePoint,
    current: currentMousePoint,

    setStart: function (
      p: { x: number; y: number; isDown?: boolean },
      isDown?: boolean
    ) {
      startMousePoint.x = p.x;
      startMousePoint.y = p.y;
      startMousePoint.isDown =
        typeof isDown == "boolean"
          ? isDown
          : typeof p.isDown == "boolean"
          ? p.isDown
          : false;
      this.setCurrent(p, isDown);
    },

    setCurrent: (
      p: { x: number; y: number; isDown?: boolean },
      isDown?: boolean
    ) => {
      currentMousePoint.x = p.x;
      currentMousePoint.y = p.y;
      currentMousePoint.isDown =
        typeof isDown == "boolean"
          ? isDown
          : typeof p.isDown == "boolean"
          ? p.isDown
          : false;
    }
  };
}

// ** Mouse Events **
export function mouseMove$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "mousemove", useCapture);
}
export function mouseDown$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "mousedown", useCapture);
}
export function mouseUp$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "mouseup", useCapture);
}
export function mouseOver$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "mouseover", useCapture);
}
export function mouseOut$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "mouseout", useCapture);
}
export function mouseEnter$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "mouseenter", useCapture);
}
export function mouseLeave$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "mouseleave", useCapture);
}
export function mouseClick$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "click", useCapture);
}
export function mouseDoubleClick$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "dblclick", useCapture);
}
export function mouseWheel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(
    el,
    "onwheel" in document.createElement("div")
      ? "wheel" // modern browsers
      : document.onmousewheel !== undefined
      ? "mousewheel" // Webkit and IE
      : "DOMMouseScroll", // older Firefox
    useCapture
  );
}
export function mouseContextMenu$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return useEvent$(el, "contextmenu", useCapture);
}

// ** Touch Events **
export function touchMove$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return useEvent$(el, "touchmove", useCapture);
}
export function touchStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return useEvent$(el, "touchstart", useCapture);
}
export function touchEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return useEvent$(el, "touchend", useCapture);
}
export function touchCancel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return useEvent$(el, "touchcancel", useCapture);
}

// ** Pointer Events **
export function pointerMove$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return useEvent$(el, "pointermove", useCapture);
}
export function pointerDown$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return useEvent$(el, "pointerdown", useCapture);
}
export function pointerUp$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return useEvent$(el, "pointerup", useCapture);
}
export function pointerCancel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return useEvent$(el, "pointercancel", useCapture);
}

// ** Hammer Events **  https://hammerjs.github.io/
// Pan : Recognized when the pointer is down and moved in the allowed direction.
export function pan$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("pan", next);
    },
    (next) => {
      hammer.off("pan", next);
    }
  );
  // return new Observable((observer) => {
  //   const pan = (e: HammerInput) => {
  //     observer.next(e);
  //   };
  //   hammer.on("pan", pan);
  //   return () => {
  //     hammer.off("pan", pan);
  //   };
  // });
}
export function panMove$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("panmove", next);
    },
    (next) => {
      hammer.off("panmove", next);
    }
  );
}
export function panStart$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("panstart", next);
    },
    (next) => {
      hammer.off("panstart", next);
    }
  );
}
export function panEnd$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("panend", next);
    },
    (next) => {
      hammer.off("panend", next);
    }
  );
}
export function panCancel$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("pancancel", next);
    },
    (next) => {
      hammer.off("pancancel", next);
    }
  );
}
export function panLeft$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("panleft", next);
    },
    (next) => {
      hammer.off("panleft", next);
    }
  );
}
export function panRight$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("panright", next);
    },
    (next) => {
      hammer.off("panright", next);
    }
  );
}
export function panUp$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("panup", next);
    },
    (next) => {
      hammer.off("panup", next);
    }
  );
}
export function panDown$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("pandown", next);
    },
    (next) => {
      hammer.off("pandown", next);
    }
  );
}
// 缩放 Pinch : Recognized when two or more pointers are moving toward (zoom-in) or away from each other (zoom-out).
export function pinch$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("pinch", next);
    },
    (next) => {
      hammer.off("pinch", next);
    }
  );
}
export function pinchIn$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("pinchin", next);
    },
    (next) => {
      hammer.off("pinchin", next);
    }
  );
}
export function pinchOut$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("pinchout", next);
    },
    (next) => {
      hammer.off("pinchout", next);
    }
  );
}
// 长按 Press : Recognized when the pointer is down for x ms without any movement.
export function press$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("press", next);
    },
    (next) => {
      hammer.off("press", next);
    }
  );
}
// 旋转 Rotate : Recognized when two or more pointer are moving in a circular motion.
export function rotate$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("rotate", next);
    },
    (next) => {
      hammer.off("rotate", next);
    }
  );
}
// Swipe : Recognized when the pointer is moving fast (velocity), with enough distance in the allowed direction.
export function swipe$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("swipe", next);
    },
    (next) => {
      hammer.off("swipe", next);
    }
  );
}
export function swipeLeft$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("swipeleft", next);
    },
    (next) => {
      hammer.off("swipeleft", next);
    }
  );
}
export function swipeRight$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("swiperight", next);
    },
    (next) => {
      hammer.off("swiperight", next);
    }
  );
}
export function swipeUp$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("swipeup", next);
    },
    (next) => {
      hammer.off("swipeup", next);
    }
  );
}
export function swipeDown$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("swipedown", next);
    },
    (next) => {
      hammer.off("swipedown", next);
    }
  );
}
// Tap : Recognized when the pointer is doing a small tap/click.
export function tap$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(el);
  return fromEventPattern(
    (next) => {
      hammer.on("tap", next);
    },
    (next) => {
      hammer.off("tap", next);
    }
  );
}
// 在 el 之外点击
export function tapOutSide$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const hammer = new Hammer(document.documentElement);
  return fromEventPattern(
    (next) => {
      const handler = (e: HammerInput) => {
        if (!e.target || el == e.target || el.contains(e.target as Node)) {
          return;
        }
        next(e);
      };
      hammer.on("tap", handler);
      return handler;
    },
    (next, handler) => {
      hammer.off("tap", handler);
    }
  );
}

// ** Transition Events **
export function transitionStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TransitionEvent> {
  return useEvent$(el, "transitionstart", useCapture);
}
export function transitionEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TransitionEvent> {
  return merge(
    useEvent$(el, "transitionend", useCapture) as Observable<TransitionEvent>,
    useEvent$(
      el,
      "webkitTransitionEnd",
      useCapture
    ) as Observable<TransitionEvent>,
    useEvent$(el, "oTransitionEnd", useCapture) as Observable<TransitionEvent>,
    useEvent$(el, "MSTransitionEnd", useCapture) as Observable<TransitionEvent>
  );
}

// ** Animation Events **
export function animationStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<AnimationEvent> {
  return useEvent$(el, "animationstart", useCapture);
}
export function animationEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<AnimationEvent> {
  return useEvent$(el, "animationend", useCapture);
}

// ** Drag Drop Events **
export function drag$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return useEvent$(el, "drag", useCapture);
}
export function dragStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return useEvent$(el, "dragstart", useCapture);
}
export function dragEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return useEvent$(el, "dragend", useCapture);
}
export function drop$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return useEvent$(el, "drop", useCapture);
}
export function dragEnter$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return useEvent$(el, "dragenter", useCapture);
}
export function dragLeave$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return useEvent$(el, "dragleave", useCapture);
}
export function dragOver$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return useEvent$(el, "dragover", useCapture);
}

// ** Keyboard Events **
export function keyDown$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<KeyboardEvent> {
  return useEvent$(el, "keydown", useCapture);
}
export function keyUp$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<KeyboardEvent> {
  return useEvent$(el, "keyup", useCapture);
}
export function keyPress$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<KeyboardEvent> {
  return useEvent$(el, "keypress", useCapture);
}

////////////////////// Mouse 或 Touch 或 Pointer 或 hammer //////////////////////

type MousePointMoveOptions = {
  start?: (e: MousePointEvent, mpg: MousePointGroup) => void;
  end?: (e: MousePointEvent, mpg: MousePointGroup) => void;
  useMousePoint?: ReturnType<typeof useMousePoint>;
};

export function useMouseMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: Pick<MousePointMoveOptions, "useMousePoint">
): Observable<MousePointGroup> {
  const mpg = options?.useMousePoint || useMousePoint();
  return mouseMove$(el).pipe(
    map((e) => {
      mpg.setCurrent(getMousePositionInElement(e, el));
      return mpg;
    })
  );
}

export function useMouseDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.useMousePoint || useMousePoint();
  return mouseMove$(el).pipe(
    windowToggle(
      mouseDown$(el).pipe(
        tap((e) => {
          mpg.setStart(getMousePositionInElement(e, el), true);
          options?.start?.(e, mpg);
        })
      ),
      () =>
        mouseUp$(el).pipe(
          tap((e) => {
            mpg.setCurrent(getMousePositionInElement(e, el), false);
            options?.end?.(e, mpg);
          })
        )
    ),
    switchAll(),
    map((e) => {
      mpg.setCurrent(getMousePositionInElement(e, el), true);
      return mpg;
    })
  );
}

export function useTouchDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.useMousePoint || useMousePoint();
  return touchStart$(el).pipe(
    tap((e) => {
      const touch = e.changedTouches[0]; // first touch
      mpg.setStart(getMousePositionInElement(touch, el), true);
      options?.start?.(touch, mpg);
    }),
    switchMap(() =>
      touchMove$(el).pipe(
        takeUntil(
          race(touchEnd$(el), touchCancel$(el)).pipe(
            tap((e) => {
              const touch = e.changedTouches[0]; // first touch
              mpg.setCurrent(getMousePositionInElement(touch, el), false);
              options?.end?.(touch, mpg);
            })
          )
        )
      )
    ),
    map((e) => {
      const touch = e.changedTouches[0]; // first touch
      mpg.setCurrent(getMousePositionInElement(touch, el), true);
      return mpg;
    })
  );
}

// 在 mobile 下似乎 pointerUp$ pointerCancel$ 会提前触发？
export function usePointerDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.useMousePoint || useMousePoint();
  return pointerDown$(el).pipe(
    tap((e) => {
      mpg.setStart(getMousePositionInElement(e, el), true);
      options?.start?.(e, mpg);
    }),
    switchMap(() =>
      pointerMove$(el).pipe(
        takeUntil(
          race(pointerUp$(el), pointerCancel$(el)).pipe(
            tap((e) => {
              mpg.setCurrent(getMousePositionInElement(e, el), false);
              options?.end?.(e, mpg);
            })
          )
        )
      )
    ),
    map((e) => {
      mpg.setCurrent(getMousePositionInElement(e, el), true);
      return mpg;
    })
  );
}

// 大一统
export function useCompatableDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  return race(
    useMouseDownMovePoint$(el, options),
    useTouchDownMovePoint$(el, options)
    // usePointerDownMovePoint$(el, options),
  );
}

export function useMouseAndPointerDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.useMousePoint || useMousePoint();
  return merge(mouseMove$(el), pointerDown$(el)).pipe(
    exhaustMap((e) => {
      if (e instanceof PointerEvent) {
        // pointerDown
        mpg.setStart(getMousePositionInElement(e, el), true);
        options?.start?.(e, mpg);
        return pointerMove$(el).pipe(
          takeUntil(
            race(pointerUp$(el), pointerCancel$(el)).pipe(
              tap((e) => {
                mpg.setCurrent(getMousePositionInElement(e, el), false);
                options?.end?.(e, mpg);
              })
            )
          )
        );
      } else if (e instanceof MouseEvent) {
        // mouseMove
        return of(e);
      } else {
        return EMPTY;
      }
    }),
    map((e) => {
      mpg.setCurrent(
        getMousePositionInElement(e, el),
        e instanceof PointerEvent
      );
      return mpg;
    })
  );
}

/**
 * 下拉刷新 pull-downing dStartY > 0
 * 上拉加载 pull-uping dStartY < 0
 */
export function usePull$(el: HTMLElement): Observable<number> {
  return new Observable<number>((observer) => {
    const subscription = usePointerDownMovePoint$(el, {
      start: () => {
        observer.next(0);
      }
    }).subscribe((mpg) => {
      observer.next(mpg.current.y - mpg.start.y);
    });
    return subscription.unsubscribe;
  }).pipe(distinctUntilChanged());
}

// <div draggable="true" ondragstart="dragstart" ondragend="dragend">Draggable-Item</div>
export function useDraggable(
  el: HTMLElement,
  options?: {
    onStart?: (e: DragEvent) => void;
    onDrag?: (e: DragEvent) => void;
    onEnd?: (e: DragEvent) => void;
  }
): () => void {
  atomWriteAttribute(el, "draggable", "true");

  const dragStartSubscription = dragStart$(el, true).subscribe(
    (e: DragEvent) => {
      preventDefault(e);
      stopPropagation(e);
      const dt = e.dataTransfer;
      if (dt) {
        dt.effectAllowed = "move";
        // you must set some data in order to drag an arbitrary block element like a <div>
        dt.setData("text/plain", "some-data");
        // const coords = { x: e.offsetX, y: e.offsetY };
        // if (dt.setDragImage) {
        //   dt.setDragImage(el, coords.x, coords.y);
        // }
      }
      options?.onStart?.(e);
    }
  );

  const dragSubscription = drag$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onDrag?.(e);
  });

  const dragEndSubscription = dragEnd$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onEnd?.(e);
  });

  return () => {
    dragStartSubscription.unsubscribe();
    dragSubscription.unsubscribe();
    dragEndSubscription.unsubscribe();
  };
}

// <div ondragenter="dragenter" ondragleave="dragleave" ondragover="dragover" ondrop="drop">Drop-Zone</div>
export function useDrop(
  el: HTMLElement,
  options?: {
    onEnter?: (e: DragEvent) => void;
    onLeave?: (e: DragEvent) => void;
    onOver?: (e: DragEvent) => void;
    onDrop?: (e: DragEvent, files: File[]) => void;
  }
): () => void {
  const dragEnterSubscription = dragEnter$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onEnter?.(e);
  });

  const dragOverSubscription = dragOver$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    const dt = e.dataTransfer;
    if (dt) {
      dt.dropEffect = "move";
    }
    options?.onOver?.(e);
  });

  const dragLeaveSubscription = dragLeave$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onLeave?.(e);
  });

  const dropSubscription = drop$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    const dt = e.dataTransfer;
    if (!dt) {
      options?.onDrop?.(e, []);
      return;
    }
    options?.onDrop?.(e, [...dt.files]);
    // const data = dt.getData("text");
    // dt.clearData();
    // for (const item of dt.items) {
    //   if (item.kind === "string") {
    //     if (item.type.match("^text/plain")) {
    //       item.getAsString((s) => {
    //         // string
    //       });
    //     } else if (item.type.match("^text/html")) {
    //       // HTML
    //     } else if (item.type.match("^text/uri-list")) {
    //       // URI
    //     }
    //   } else if (item.kind === "file") {
    //     if (item.type.match("^image/")) {
    //       // image file
    //     }
    //     const f = item.getAsFile();
    //   }
    // }
  });

  return () => {
    dragEnterSubscription.unsubscribe();
    dragOverSubscription.unsubscribe();
    dragLeaveSubscription.unsubscribe();
    dropSubscription.unsubscribe();
  };
}

////////////////////// Global Events //////////////////////

export function useScroll$(time = 10): Observable<Event> {
  // window.scrollX;
  // window.scrollY;
  return useEvent$(window, "scroll", true).pipe(throttleTime(time));
}

export function useDocumentVisible$(): Observable<boolean> {
  return useEvent$(document, "visibilitychange", true).pipe(
    map(() => {
      // return !document.hidden;
      return document.visibilityState === "visible";
    }),
    distinctUntilChanged()
  );
  // return combineLatest([
  //   useEvent$(window, "focus", true),
  //   useEvent$(window, "pagehide", true),
  //   useEvent$(window, "blur", true)
  // ]).pipe(
  //   map(([_, __, ___]) => {
  //     return !document.hidden;
  //   }),
  //   distinctUntilChanged()
  // );
}

// 监听 localStorage , storageEvent.key == localStorage.key
export function storage$(): Observable<StorageEvent> {
  return useEvent$<StorageEvent>(window, "storage", false);
}

////////////////////// Position //////////////////////

export function getWindow() {
  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    innerWidth: window.innerWidth, // 浏览器窗口的宽度（不包括滚动条）
    innerHeight: window.innerHeight, // 浏览器窗口的高度（不包括滚动条）
    screenX: window.screenX,
    screenY: window.screenY, // window.screenY + (window.outerHeight - window.innerHeight)
    pageXOffset: window.pageXOffset, // 当前页面水平滚动的像素数
    pageYOffset: window.pageYOffset // 当前页面垂直滚动的像素数
  };
}

// For any non-empty HTML document, documentElement will always be an <html> element.
// For any non-empty XML document, documentElement will always be whatever element is the root element of the document.
// The Document.body property represents the <body> or <frameset> node of the current document, or null if no such element exists.
export function getDocumentElement() {
  const el = document.documentElement || document.body;
  // // IE浏览器
  // if (document.compatMode === "CSS1Compat") {
  //   // 标准模式
  // } else {
  //   // 怪异模式
  // }
  return {
    el,

    // 不包括边框大小，只包含内容和内边距
    clientWidth: el.clientWidth,
    clientHeight: el.clientHeight,
    clientTop: el.clientTop,
    clientLeft: el.clientLeft,

    // const contentWidth =
    //   [...el.children].reduce(
    //     (a, e) => Math.max(a, e.getBoundingClientRect().right),
    //     0
    //   ) - el.getBoundingClientRect().x;

    // 元素的内容区域加上它的内边距再加上任何溢出内容的尺寸
    // 当内容没有溢出时，scrollWidth == clientWidth , scrollHeight == clientHeight
    // 当内容溢出时，   scrollWidth >  clientWidth , scrollHeight >  clientHeight
    scrollWidth: el.scrollWidth,
    scrollHeight: el.scrollHeight,
    scrollTop: el.scrollTop,
    scrollLeft: el.scrollLeft,

    // 返回元素的实际大小，包括边框、内边距和滚动条
    offsetWidth: el.offsetWidth,
    offsetHeight: el.offsetHeight,
    offsetTop: el.offsetTop,
    offsetLeft: el.offsetLeft,

    offsetParent: el.offsetParent
  };
}

// 获取可视区域
export function getViewport() {
  const { clientWidth, clientHeight, scrollLeft, scrollTop } =
    getDocumentElement();
  return {
    width: window.innerWidth || clientWidth,
    height: window.innerHeight || clientHeight,
    x: window.pageXOffset || scrollLeft,
    y: window.pageYOffset || scrollTop
  };
}

// 获取页面整体
export function getPage() {
  const { scrollWidth, scrollHeight, scrollLeft, scrollTop } =
    getDocumentElement();
  return {
    width: scrollWidth,
    height: scrollHeight,
    scrollX: window.pageXOffset || scrollLeft,
    scrollY: window.pageYOffset || scrollTop
  };
}

export function getElementPositionInViewport(el: HTMLElement) {
  return el.getBoundingClientRect();
}

export function getRelativeBoundingClientRect(child: HTMLElement) {
  return {
    x: child.offsetLeft,
    y: child.offsetTop,
    width: child.offsetWidth,
    height: child.offsetHeight
  };
}

export function getElementPositionInPage(
  el: HTMLElement | SVGElement | Element
) {
  const { top, left, width, height } = el.getBoundingClientRect();
  const { scrollLeft, scrollTop } = getDocumentElement();
  return {
    x: left + scrollLeft,
    y: top + scrollTop,
    width,
    height
  };
}

export function getElementPositionInPage2(el: HTMLElement) {
  let elem: HTMLElement | null = el;
  let actualLeft = 0;
  let actualTop = 0;
  while (elem !== null) {
    actualLeft += elem.offsetLeft;
    actualTop += elem.offsetTop;
    if (elem.offsetParent instanceof HTMLElement) {
      elem = elem.offsetParent;
    } else {
      elem = null;
    }
  }
  return {
    x: actualLeft,
    y: actualTop,
    width: el.offsetWidth,
    height: el.offsetHeight
  };
}

export function getVerticalScrollTopPercentInPage() {
  const { clientHeight, scrollTop, scrollHeight } = getDocumentElement();
  const height = scrollHeight - clientHeight;
  const percent = height <= 0 ? 0 : (scrollTop / height) * 100;
  return percent;
}

// 判断元素是否在视口内
export function isInViewport(el: HTMLElement): boolean {
  const viewport = getViewport();
  const rect = getElementPositionInViewport(el);
  return (
    rect.bottom >= 0 &&
    rect.top <= viewport.height &&
    rect.right >= 0 &&
    rect.left <= viewport.width
  );
}

// export function getComputedPosition(el: HTMLElement) {
//   const styles = atomReadStyles(el);
//   const transform = readStyleFromStyles(styles, "transform");
//   let x = 0;
//   let y = 0;
//   if (transform && transform !== "none") {
//     // 匹配 matrix() matrix3d()
//     const matrixValues = transform.match(/matrix[^()]*\(([^()]+)\)/);
//     if (matrixValues && matrixValues[1]) {
//       const matrix = matrixValues[1].split(", ").map(Number);
//       x = +(matrix[12] || matrix[4]) || 0;
//       y = +(matrix[13] || matrix[5]) || 0;
//     }

//     // 匹配 translateX() 和 translateY()
//     const translateX = transform.match(/translateX\((-?[\d.]+)px\)/);
//     const translateY = transform.match(/translateY\((-?[\d.]+)px\)/);
//     if (translateX) {
//       x = parseFloat(translateX[1]);
//     }
//     if (translateY) {
//       y = parseFloat(translateY[1]);
//     }

//     // 匹配 translate()
//     const translate = transform.match(
//       /translate\((-?[\d.]+)px, (-?[\d.]+)px\)/
//     );
//     if (translate) {
//       x = parseFloat(translate[1]);
//       y = parseFloat(translate[2]);
//     }

//     // 匹配 scaleX() 和 scaleY()
//     // const scaleX = transform.match(/scaleX\((-?[\d.]+)\)/);
//     // const scaleY = transform.match(/scaleY\((-?[\d.]+)\)/);

//     // 匹配 rotate()
//     // const rotate = transform.match(/rotate\((-?[\d.]+)deg\)/);
//   } else {
//     x = +styles.left.replace(/[^-\d.]/g, "") || 0;
//     y = +styles.top.replace(/[^-\d.]/g, "") || 0;
//   }
//   return { x: x, y: y };
// }

////////////////////// Position : Mouse 或 Touch 或 Pointer 或 hammer //////////////////////

export type MousePointEvent = MouseEvent | Touch | PointerEvent | HammerInput;

export function getMousePositionInViewport(ev: MousePointEvent) {
  if ("center" in ev) {
    const center = (ev as HammerInput).center;
    const rect = getElementPositionInViewport(ev.target);
    return {
      x: center.x - rect.x,
      y: center.y - rect.y
    };
  }
  return {
    x: ev.clientX,
    y: ev.clientY
  };
}

export function getMousePositionInPage(ev: MousePointEvent) {
  if ("center" in ev) {
    return (ev as HammerInput).center;
  }
  if (typeof ev.pageX == "number" && typeof ev.pageY == "number") {
    return {
      x: ev.pageX,
      y: ev.pageY
    };
  }
  const { scrollLeft, scrollTop } = getDocumentElement();
  return {
    x: ev.clientX + scrollLeft,
    y: ev.clientY + scrollTop
  };
}

export function getMousePositionInElement(
  ev: MousePointEvent,
  element?: AnyElement
) {
  const r = {
    x: 0,
    y: 0
  };
  if (!element) {
    /// 原 offset 只是相对于直接父类
    let fixedOffsetX = 0;
    let fixedOffsetY = 0;
    if (
      "currentTarget" in ev &&
      ev.currentTarget &&
      ev.target &&
      ev.currentTarget != ev.target
      // ev.target: Refers to the actual element where the event occurred
      // ev.currentTarget: Refers to the element to which the event handler is attached
    ) {
      const currentTargetRect = (
        ev.currentTarget as AnyElement
      ).getBoundingClientRect();
      const targetRect = (ev.target as AnyElement).getBoundingClientRect();
      fixedOffsetX = targetRect.left - currentTargetRect.left;
      fixedOffsetY = targetRect.top - currentTargetRect.top;
    }
    if ("offsetX" in ev) {
      r.x = ev.offsetX + fixedOffsetX;
      r.y = ev.offsetY + fixedOffsetY;
    } else {
      const mouse = getMousePositionInPage(ev);
      const p = getElementPositionInPage(ev.target as AnyElement);
      r.x = clamp(mouse.x - p.x + fixedOffsetX, 0, p.width);
      r.y = clamp(mouse.y - p.y + fixedOffsetY, 0, p.height);
    }
  } else {
    const mouse = getMousePositionInPage(ev);
    const p = getElementPositionInPage(element);
    r.x = clamp(mouse.x - p.x, 0, p.width);
    r.y = clamp(mouse.y - p.y, 0, p.height);
  }
  return r;
}

/**
 * Given a mouse scroll wheel event, get the "delta" of how fast it moved.
 * @param {Object} e - a DOM Event Object.
 * @return {int} - the mouse wheel's delta.  It is greater than 0, the
 * scroll wheel was spun upwards; if less than 0, downwards.
 */
export function getScrollWheelDelta(e: any) {
  let delta = 0;
  if (!e) /* For IE. */ e = window.event;
  if (e.wheelDelta) {
    /* IE/Opera. */
    delta = e.wheelDelta / 120;
    /** In Opera 9, delta differs in sign as compared to IE.
     */
    if ((window as any).opera) {
      delta = -delta;
    }
  } else if (e.detail) {
    /** Mozilla case. */
    /** In Mozilla, sign of delta is different than in IE.
     * Also, delta is multiple of 3.
     */
    delta = -e.detail / 3;
  }
  return delta;
}

// 0 — Left Button (the default)
// 1 — Middle Button (currently unsupported)
// 2 — Right Button
// 3 — X1 (Back) Button
// 4 — X2 (Forward) Button
export function getMouseButton(event: MouseEvent) {
  if (document.implementation.hasFeature("MouseEvents", "2.0")) {
    return event.button;
  } else {
    switch (event.button) {
      case 0:
      case 1:
      case 3:
      case 5:
      case 7:
        return 0; // 左键
      case 2:
      case 6:
        return 2; // 右键
      case 4:
        return 1; // 中键
    }
  }
}

////////////////////// Keyboard //////////////////////

export const CommonKeyCode = {
  BACKSPACE: 8,
  TAB: 9,
  COMMA: 188,
  DELETE: 46,
  END: 35,
  ENTER: 13,
  ESCAPE: 27,
  HOME: 36,
  LEFT: 37,
  UP: 38,
  DOWN: 40,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  PERIOD: 190,
  RIGHT: 39,
  SPACE: 32
};

/**
 * Representations of pressable keys that aren't text.  These are stored in
 * the Unicode PUA (Private Use Area) code points, 0xE000-0xF8FF.  Refer to
 * http://www.google.com.au/search?&q=unicode+pua&btnK=Search
 *
 * @enum {string}
 * @see <https://www.w3.org/TR/webdriver/#keyboard-actions>
 */
export const Key = {
  NULL: "\uE000",
  CANCEL: "\uE001", // ^break
  HELP: "\uE002",
  BACK_SPACE: "\uE003",
  TAB: "\uE004",
  CLEAR: "\uE005",
  RETURN: "\uE006",
  ENTER: "\uE007",
  SHIFT: "\uE008",
  CONTROL: "\uE009",
  ALT: "\uE00A",
  PAUSE: "\uE00B",
  ESCAPE: "\uE00C",
  SPACE: "\uE00D",
  PAGE_UP: "\uE00E",
  PAGE_DOWN: "\uE00F",
  END: "\uE010",
  HOME: "\uE011",
  ARROW_LEFT: "\uE012",
  LEFT: "\uE012",
  ARROW_UP: "\uE013",
  UP: "\uE013",
  ARROW_RIGHT: "\uE014",
  RIGHT: "\uE014",
  ARROW_DOWN: "\uE015",
  DOWN: "\uE015",
  INSERT: "\uE016",
  DELETE: "\uE017",
  SEMICOLON: "\uE018",
  EQUALS: "\uE019",

  NUMPAD0: "\uE01A", // number pad keys
  NUMPAD1: "\uE01B",
  NUMPAD2: "\uE01C",
  NUMPAD3: "\uE01D",
  NUMPAD4: "\uE01E",
  NUMPAD5: "\uE01F",
  NUMPAD6: "\uE020",
  NUMPAD7: "\uE021",
  NUMPAD8: "\uE022",
  NUMPAD9: "\uE023",
  MULTIPLY: "\uE024",
  ADD: "\uE025",
  SEPARATOR: "\uE026",
  SUBTRACT: "\uE027",
  DECIMAL: "\uE028",
  DIVIDE: "\uE029",

  F1: "\uE031", // function keys
  F2: "\uE032",
  F3: "\uE033",
  F4: "\uE034",
  F5: "\uE035",
  F6: "\uE036",
  F7: "\uE037",
  F8: "\uE038",
  F9: "\uE039",
  F10: "\uE03A",
  F11: "\uE03B",
  F12: "\uE03C",

  COMMAND: "\uE03D", // Apple command key
  META: "\uE03D", // alias for Windows key

  /**
   * Japanese modifier key for switching between full- and half-width
   * characters.
   * @see <https://en.wikipedia.org/wiki/Language_input_keys>
   */
  ZENKAKU_HANKAKU: "\uE040"
};

export function getKeyCode(e: KeyboardEvent) {
  if (e.key) {
    return e.key; // e.key='u' | e.code='KeyU'
  } else if (e.keyCode) {
    return e.keyCode;
  } else if (e.which) {
    return e.which;
  }
}

// 注意：不要与浏览器系统快捷键重合！
export interface HotKeyOptions {
  keys: string[];
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  target?: EventTarget;
}

/// UNDO 判断多个 keys 同时按下
function matchHotKeyScheme(
  event: KeyboardEvent, // WheelEvent | KeyboardEvent
  options: HotKeyOptions
): boolean {
  const ctrlKey = options.ctrl || false;
  const shiftKey = options.shift || false;
  const altKey = options.alt || false;
  return (
    (ctrlKey === event.metaKey || ctrlKey === event.ctrlKey) &&
    shiftKey === event.shiftKey &&
    altKey === event.altKey &&
    options.keys.map((k) => k.toLowerCase()).includes(event.key.toLowerCase())
  );
}

// UNDO pause
// UNDO resume

// 注意：不要与浏览器系统快捷键重合！
export function useHotKeyDown$(options: HotKeyOptions): Observable<void> {
  return keyDown$(options.target || document || window).pipe(
    filter((e: KeyboardEvent) => matchHotKeyScheme(e, options)),
    map((e: KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      return;
    })
  );
}

// 注意：不要与浏览器系统快捷键重合！
export function useHotKeyUp$(options: HotKeyOptions): Observable<void> {
  return keyUp$(options.target || document || window).pipe(
    filter((e: KeyboardEvent) => matchHotKeyScheme(e, options)),
    map((e: KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      return;
    })
  );
}

// 注意：不要与浏览器系统快捷键重合！
export function useHotKeyFile$(
  hotKeyOptions: HotKeyOptions = { keys: ["o"] },
  type: ReadInBrowserEnum = ReadInBrowserEnum.text
) {
  return useHotKeyDown$(hotKeyOptions).pipe(
    // 方法1：使用 switchMap 调用异步函数
    switchMap(async () => {
      return await useSelectFile(type);
    })
    // 方法2：将 Promise 转为 Observable
    // switchMap(() => {
    //   return from(useSelectFile(type));
    // })
  );
}

// 注意：不要与浏览器系统快捷键重合！
export async function useHotKeyFile(
  hotKeyOptions: HotKeyOptions = { keys: ["o"] },
  type: ReadInBrowserEnum = ReadInBrowserEnum.text
) {
  return firstValueFrom(useHotKeyFile$(hotKeyOptions, type)); // 将 Observable 转为 Promise
}
